import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

import { getPaymentRequestId } from '@utility/checkout-utils';

import { useErrorContext } from '../../contexts/error/errorContext';
import {
  ScaPaymentDetailsContextProvider,
  useScaPaymentDetailsContext,
} from '../../contexts/ScaPaymentDetailsContext/ScaPaymentDetailsContext';
import { useJwtContext } from '../../hooks/useJwtContext/useJwtContext';
import { SCA_EVENTS } from '../../models/globals/events.constants';
import { dataLayer } from '../../utils/dataLayer';

import type { ScaSubjectProviderProps } from './ScaSubjectProvider.types';

export const PaymentDetailsProvider = ({
  children,
  skip,
}: ScaSubjectProviderProps) => {
  const paymentId = getPaymentRequestId() ?? '';
  const { token: authToken } = useJwtContext();

  return (
    <ScaPaymentDetailsContextProvider
      paymentId={paymentId ?? ''}
      authToken={authToken ?? ''}
      skip={skip || !paymentId || !authToken}
    >
      <TrackPaymentDetailsLoadingErrors />
      {children}
    </ScaPaymentDetailsContextProvider>
  );
};

export const TrackPaymentDetailsLoadingErrors = () => {
  const { loadingError } = useScaPaymentDetailsContext();
  const { setError } = useErrorContext();

  useEffect(() => {
    if (loadingError) {
      setError(loadingError);
      dataLayer.trackEvent({
        name: SCA_EVENTS.GENERIC_ERROR,
        properties: {
          code: loadingError.name,
          message: loadingError.message,
        },
      });
    }
  }, [loadingError, setError]);

  return null;
};

export const withPaymentDetailsProvider = ({ children }: PropsWithChildren) => (
  <PaymentDetailsProvider>{children}</PaymentDetailsProvider>
);
